import { createContext, useCallback, useContext, useRef, useState } from 'react'

const ToastContext = createContext(null)

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const idRef = useRef(0)

  const dismiss = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id))
  }, [])

  const push = useCallback(
    (message, type = 'info', ms = 3500) => {
      const id = ++idRef.current
      setToasts((t) => [...t, { id, message, type }])
      if (ms) setTimeout(() => dismiss(id), ms)
      return id
    },
    [dismiss]
  )

  const toast = {
    info: (m, ms) => push(m, 'info', ms),
    success: (m, ms) => push(m, 'success', ms),
    error: (m, ms) => push(m, 'error', ms),
  }

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="toast-wrap" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast toast-${t.type}`}
            onClick={() => dismiss(t.id)}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>')
  return ctx
}

/**
 * Turn a Supabase/PostgREST error into human language.
 * RLS rejections and unique-constraint violations get friendly copy.
 */
export function humanizeError(error) {
  if (!error) return 'Something went wrong. Please try again.'
  const msg = error.message || String(error)
  const code = error.code

  if (code === '42501' || /row-level security|permission denied/i.test(msg)) {
    return "You don't have permission to do this."
  }
  if (code === '23505' || /duplicate key|already exists/i.test(msg)) {
    return 'This entry already exists.'
  }
  if (/Failed to fetch|NetworkError/i.test(msg)) {
    return 'Network problem. Check your connection and try again.'
  }
  if (/Invalid login credentials/i.test(msg)) {
    return 'Email or password is incorrect.'
  }
  if (/Email not confirmed/i.test(msg)) {
    return 'Please confirm your email first, then log in.'
  }
  if (/User already registered/i.test(msg)) {
    return 'An account with this email already exists. Try logging in.'
  }
  return msg
}
